import * as THREE from 'three'
import { OrbitControls } from 'three/addons/controls/OrbitControls.js'
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js'
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js'
import GUI from 'lil-gui'
import gsap from 'gsap'
import particlesVertexShader from './shaders/particles/vertex.glsl'
import particlesFragmentShader from './shaders/particles/fragment.glsl'
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js'

/**
 * Base
 */
// Debug
const gui = new GUI({ width: 340 })
const debugObject = {}

// Canvas
const canvas = document.querySelector('canvas.webgl')

// Scene
const scene = new THREE.Scene()

// Loaders
const dracoLoader = new DRACOLoader()
dracoLoader.setDecoderPath('./draco/')
const gltfLoader = new GLTFLoader()
gltfLoader.setDRACOLoader(dracoLoader)

/**
 * Sizes
 */
const sizes = {
    width: window.innerWidth,
    height: window.innerHeight,
    pixelRatio: Math.min(window.devicePixelRatio, 2)
}

window.addEventListener('resize', () =>
{
    // Update sizes
    sizes.width = window.innerWidth
    sizes.height = window.innerHeight
    sizes.pixelRatio = Math.min(window.devicePixelRatio, 2)

    // Materials
    if(particles){
        particles.material.uniforms.uResolution.value.set(sizes.width * sizes.pixelRatio, sizes.height * sizes.pixelRatio)
    }

    // Update camera
    camera.aspect = sizes.width / sizes.height
    camera.updateProjectionMatrix()

    // Update renderer
    renderer.setSize(sizes.width, sizes.height)
    renderer.setPixelRatio(sizes.pixelRatio)
})

/**
 * Camera
 */
// Base camera
const camera = new THREE.PerspectiveCamera(35, sizes.width / sizes.height, 0.1, 100)
camera.position.set(0, 0, 9 * 2)
scene.add(camera)

// Controls
const controls = new OrbitControls(camera, canvas)
controls.enableDamping = true

/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
    canvas: canvas,
    antialias: true,
})

renderer.setSize(sizes.width, sizes.height)
renderer.setPixelRatio(sizes.pixelRatio)

debugObject.clearColor = '#160920'
gui.addColor(debugObject, 'clearColor').onChange(() => { renderer.setClearColor(debugObject.clearColor) })
renderer.setClearColor(debugObject.clearColor)

/**
 * Particles
 */

//Load models
let particles = null
gltfLoader.load('./star_badge_key_REMESH.glb', (gltf) => {
//gltfLoader.load('./models.glb', (gltf) => {
    particles = {}
    particles.index = 0

// positions
const positions = gltf.scene.children
  .map((child) => {
    console.log("child", child.name)

    const geometries = []

    child.traverse((node) => {
      if (node.isMesh && node.geometry?.attributes?.position) {
        const g = node.geometry.clone()

        g.applyMatrix4(node.matrixWorld)

        geometries.push(g)
      }
    })

    if (geometries.length === 0) return null

    const merged = mergeGeometries(geometries, false)

    return merged.attributes.position
  })
  .filter(Boolean)

console.log("positions", positions)

console.log("positions", positions)

particles.maxCount = 0
for(const position of positions){
    if(position.count > particles.maxCount){
        particles.maxCount = position.count
    }
}
console.log("maxCount", particles.maxCount)

particles.positions = []
for(const position of positions){
    const originalArray = position.array
    const newArray = new Float32Array(particles.maxCount * 3)

    const offsetY = 0

    for(let i =0; i < particles.maxCount; i++){
        const i3 = i * 3
        if(i3 < originalArray.length){
        newArray[i3] = originalArray[i3]
        newArray[i3 + 1] = originalArray[i3 + 1] + offsetY
        newArray[i3 + 2] = originalArray[i3 + 2]
        }else{
            const randomIndex = Math.floor(position.count * Math.random()) * 3
            newArray[i3 + 0] = originalArray[randomIndex + 0]
            newArray[i3 + 1] = originalArray[randomIndex + 1] + offsetY
            newArray[i3 + 2] = originalArray[randomIndex + 2]
        }
    }
    particles.positions.push(new THREE.Float32BufferAttribute(newArray, 3))
}
console.log("positions", particles.positions)


// Geometry

const sizesArray = new Float32Array(particles.maxCount)
for(let i = 0; i < particles.maxCount; i++){
    sizesArray[i] = Math.random()
}


particles.geometry = new THREE.BufferGeometry()
particles.geometry.setAttribute('position', particles.positions[particles.index])
particles.geometry.setAttribute('aPositionTarget', particles.positions[0])
particles.geometry.setAttribute('aSize', new THREE.BufferAttribute(sizesArray, 1))
 



// Material
particles.colorA = '#ff7300'
particles.colorB = '#0091ff'

particles.material = new THREE.ShaderMaterial({
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    vertexShader: particlesVertexShader,
    fragmentShader: particlesFragmentShader,
    uniforms:
    {
        uSize: new THREE.Uniform(0.15),
        uResolution: new THREE.Uniform(new THREE.Vector2(sizes.width * sizes.pixelRatio, sizes.height * sizes.pixelRatio)),
        uProgress: new THREE.Uniform(0.0),
        uColorA: new THREE.Uniform(new THREE.Color(particles.colorA)),
        uColorB: new THREE.Uniform(new THREE.Color(particles.colorB))
    }
})
// Points
particles.points = new THREE.Points(particles.geometry, particles.material)
particles.points.frustumCulled = false
scene.add(particles.points)

// Methods
particles.morph = (index) => {
    
    // Update attributes
    particles.geometry.attributes.position =  particles.positions[particles.index]
    particles.geometry.attributes.aPositionTarget = particles.positions[index]

    // Animate uProgress
    gsap.fromTo(
        particles.material.uniforms.uProgress,
        { value: 0 },
        { value: 1, duration: 3, ease: 'linear' }
    )
    particles.index = index
}
particles.key = () => particles.morph(0)
particles.stars = () => particles.morph(1)
particles.badge = () => particles.morph(2)
particles.brand = () => particles.morph(3)
particles.signiture = () => particles.morph(4)

//Tweaks
gui.addColor(particles, 'colorA').onChange(() => particles.material.uniforms.uColorA.value.set(particles.colorA))
gui.addColor(particles, 'colorB').onChange(() => particles.material.uniforms.uColorB.value.set(particles.colorB))

gui.add(particles.material.uniforms.uProgress, 'value').min(0).max(1).step(0.001).name('uProgress').listen()
gui.add(particles, 'key')
gui.add(particles, 'stars')
gui.add(particles, 'badge')
gui.add(particles, 'brand')
gui.add(particles, 'signiture')
})

/**
 * Animate
 */
const tick = () =>
{
    // Update controls
    controls.update()

    // Render normal scene
    renderer.render(scene, camera)

    // Call tick again on the next frame
    window.requestAnimationFrame(tick)
}

tick()